import { ColDef } from 'ag-grid-community';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CRUDSettings from '../components/General/CRUDSettings';
import { useBrands, useCreateBrand, useDeleteBrand, useUpdateBrand } from '../hooks/api/brands';
import { Brand } from '../types/Brand.interface';
import ConfirmationDialog from '../components/UI/Dialogs/ConfirmationDialog';

const Brands = () => {
    const { t } = useTranslation();
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [selectedBrand, setSelectedBrand] = useState<Brand | undefined>(undefined);

    const { data: brands, isLoading: loading } = useBrands();
    const { mutate: handleCreateBrand } = useCreateBrand({
        onSettled: () => {},
    });
    const { mutate: handleUpdateBrand } = useUpdateBrand({
        onSettled: () => {},
    });
    const { mutate: handleDeleteBrand } = useDeleteBrand({
        onSettled: (data, error) => {
            if (!error) {
                setSelectedBrand(undefined);
                setShowConfirmationDialog(false);
            }
        },
    });

    const onDeleteBrand = useCallback(async (data: Brand) => {
        setSelectedBrand(data);
        setShowConfirmationDialog(true);
    }, []);

    const confirmBrandDeletion = useCallback(
        (data: Brand) => {
            handleDeleteBrand(data.id);
            setShowConfirmationDialog(false);
        },
        [handleDeleteBrand]
    );

    const columnDefs: ColDef[] = [
        {
            field: 'id',
            headerName: 'ID',
            maxWidth: 100,
            editable: false,
        },
        {
            field: 'name',
            headerName: t('field.name'),
            editable: true,
        },
    ];

    return (
        <>
            <CRUDSettings
                tableRows={brands}
                columnDefs={columnDefs}
                btnText={t('brand.add')}
                createRecord={handleCreateBrand}
                updateRecord={handleUpdateBrand}
                recordType={t('field.brand')}
                isCreationViaDialog={false}
                isEditViaDialog={false}
                loading={loading}
                onSelectedRowChange={setSelectedBrand}
                showDeleteButton={true}
                onDelete={onDeleteBrand}
                columnToFocusOnEdit={'name'}
                hideIdColumn={true}
            />
            {showConfirmationDialog && selectedBrand && (
                <ConfirmationDialog
                    open={showConfirmationDialog}
                    title={t('brand.delete')}
                    text={t('brand.deleteConfirm', {
                        brand: selectedBrand.name,
                    })}
                    onClose={() => setShowConfirmationDialog(false)}
                    onConfirm={() => confirmBrandDeletion(selectedBrand)}
                />
            )}
        </>
    );
};

export default Brands;
